import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaskSummary, TaskWorkOutcome, WorkSessionDetail } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { declarableTasksOf, defaultOutcomeFor } from '@/lib/my-tasks';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { TaskPicker } from '@/components/TaskPicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Line {
  taskId: string;
  label: string;
  minutes: number;
  outcome: TaskWorkOutcome;
}

export function MapUnmappedTimeDialog({
  session,
  open,
  onOpenChange,
}: {
  session: WorkSessionDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [lines, setLines] = React.useState<Line[]>([]);

  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    enabled: open,
    retry: false,
  });

  const openTasks: TaskSummary[] = React.useMemo(
    () => declarableTasksOf(tasksQuery.data),
    [tasksQuery.data],
  );

  const alreadyMapped = session.splits
    .filter((s) => s.taskId !== null)
    .map((s) => ({ taskId: s.taskId, minutes: s.declaredMinutes, outcome: s.outcome }));

  // Mapping onto a task the session already carries adds to it rather than
  // creating a second row, so say so before the minutes are entered.
  const existingByTask = new Map(
    session.splits
      .filter((s) => s.taskId !== null)
      .map((s) => [s.taskId as string, s.declaredMinutes]),
  );

  const assigned = lines.reduce((sum, l) => sum + (Number.isFinite(l.minutes) ? l.minutes : 0), 0);
  const remaining = session.unmappedMinutes - assigned;

  const mapMutation = useMutation({
    mutationFn: () => {
      const splits = [
        ...alreadyMapped,
        ...lines
          .filter((l) => l.minutes > 0)
          .map((l) => ({ taskId: l.taskId, minutes: l.minutes, outcome: l.outcome })),
      ];
      if (remaining > 0) {
        splits.push({ taskId: null, minutes: remaining, outcome: null });
      }
      return api.adjustWorkSession(session.id, {
        reason: 'Mapped previously unmapped time',
        clockInAt: session.clockInAt,
        clockOutAt: session.clockOutAt ?? undefined,
        breaks: session.breaks
          .filter((b) => b.endedAt !== null)
          .map((b) => ({ startedAt: b.startedAt, endedAt: b.endedAt as string })),
        splits,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['work-log'] });
      await queryClient.invalidateQueries({ queryKey: ['queue-items'] });
      setLines([]);
      onOpenChange(false);
    },
  });

  const addLine = (taskId: string): void => {
    const task = openTasks.find((t) => t.id === taskId);
    if (!task || lines.some((l) => l.taskId === taskId)) return;
    setLines((prev) => [
      ...prev,
      { taskId, label: task.title, minutes: 0, outcome: defaultOutcomeFor(task) },
    ]);
  };

  const unused = openTasks.filter((t) => !lines.some((l) => l.taskId === t.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Map your time</DialogTitle>
          <DialogDescription>
            {session.unmappedMinutes} minute(s) from {session.businessDate} are not on a task yet.
            Anything you leave here stays unmapped.
          </DialogDescription>
        </DialogHeader>

        {mapMutation.error ? (
          <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-4 py-3 text-sm">
            {toMessage(mapMutation.error)}
          </div>
        ) : null}

        <div className="space-y-3">
          {lines.map((line, i) => (
            <div key={line.taskId} className="flex items-center gap-3">
              <span className="flex-1 text-sm">
                {line.label}
                {existingByTask.has(line.taskId) ? (
                  <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                    adds to {existingByTask.get(line.taskId)} min already logged
                  </span>
                ) : null}
              </span>
              <Input
                type="number"
                min={0}
                value={line.minutes === 0 ? '' : String(line.minutes)}
                onChange={(e) => {
                  const n = e.target.value === '' ? 0 : Number.parseInt(e.target.value, 10);
                  setLines((prev) =>
                    prev.map((l, idx) =>
                      idx === i ? { ...l, minutes: Number.isNaN(n) ? 0 : n } : l,
                    ),
                  );
                }}
                placeholder="0"
                className="w-24"
                aria-label={`Minutes on ${line.label}`}
              />
              <Select
                value={line.outcome}
                onChange={(e) =>
                  setLines((prev) =>
                    prev.map((l, idx) =>
                      idx === i ? { ...l, outcome: e.target.value as TaskWorkOutcome } : l,
                    ),
                  )
                }
                className="w-36"
                aria-label={`Outcome for ${line.label}`}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </Select>
            </div>
          ))}

          {unused.length > 0 ? (
            <TaskPicker tasks={unused} onSelect={addLine} placeholder="Add a task…" />
          ) : null}

          <div className="flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-4 py-3 text-sm">
            <span>Still unmapped</span>
            <span className="tabular-nums font-medium">
              {remaining > 0
                ? `${remaining} min`
                : remaining < 0
                  ? `${-remaining} min over`
                  : '0 min'}
            </span>
          </div>

          {remaining < 0 ? (
            <p className="text-sm text-[hsl(var(--destructive))]">
              You have assigned {-remaining} more minute(s) than were unmapped.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={remaining < 0 || assigned === 0 || mapMutation.isPending}
            onClick={() => mapMutation.mutate()}
          >
            {mapMutation.isPending ? 'Saving…' : 'Save mapping'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
